import React, { useState, useMemo, useRef, useEffect } from 'react';
import { DocumentTypes } from 'types';
import { useSelector, useDispatch } from 'react-redux';
import { getDocumentDisplayedSelector, getIsSingleFile, inspection } from 'store';
import { listToRange, getValidatedRangeToList } from 'utils/pageRanges/pageRangeConversion';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { useTracker } from '../../Tracker/TrackerProvider';
import styles from './PageRangeInput.module.css';

interface PageRangeInputProps {
  documentType: DocumentTypes;
  pageRange: number[];
  totalPages: number;
}

const PageRangeInput = ({ documentType, pageRange, totalPages }: PageRangeInputProps) => {
  const tracker = useTracker();
  const dispatch = useDispatch();
  const documentsDisplayed = useSelector(getDocumentDisplayedSelector);
  const isSingleFile = useSelector(getIsSingleFile);
  const inputRef = useRef<HTMLInputElement>(null);
  const [showError, setShowError] = useState<boolean>(false);

  const convertedPageRange = useMemo<string>(() => {
    return listToRange(pageRange);
  }, [pageRange]);
  const [rangeInput, setRangeInput] = useState(convertedPageRange);

  const handleRangeChange = (value: string | null) => {
    setRangeInput(value ?? '');
    setShowError(false);
  };

  const updatePageRange = () => {
    const errorOrList = getValidatedRangeToList(rangeInput, totalPages);
    if (errorOrList instanceof Error) {
      // is an invalid range input
      setShowError(true);
    } else {
      // is valid range input
      tracker.track({
        name: `document-${documentType}-range-changed`,
        data: {
          range: errorOrList,
        },
      });

      // Find pages that were included but are now being excluded
      // These pages need to have their zones deleted (same behavior as thumbnail toggle)
      const newlyExcludedPages = pageRange.filter((existingPage) => !errorOrList.includes(existingPage));

      // Dispatch once so the re-serialize/renumber work runs a single time for the whole batch.
      if (newlyExcludedPages.length) {
        dispatch(inspection.actions.excludePages(newlyExcludedPages, documentType));
      }

      dispatch(
        inspection.actions.setPageRange({
          documentType,
          pageRange: errorOrList,
        }),
      );
      setRangeInput(listToRange(errorOrList)); // convert back again to the standard formatted range input
    }
  };

  const handleBlur = () => {
    updatePageRange();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      inputRef.current?.blur();
    }
  };

  useEffect(() => {
    setShowError(false);
  }, [rangeInput]);

  // Set ranges after loading inspection data
  useEffect(() => {
    if (
      documentsDisplayed &&
      document.activeElement !== inputRef.current // input should not be overwritten when user has input focused
    ) {
      setRangeInput(listToRange(pageRange));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsDisplayed, pageRange]);

  const docTypeLabel = documentType === DocumentTypes.target ? 'new' : documentType;
  const label = isSingleFile ? 'Enter page range' : docTypeLabel;

  return (
    <GVTextField
      name={`${documentType}RangeValue`}
      id={`${documentType}RangeValue`}
      data-testid={`${documentType}_page_range_field`}
      className={styles.root}
      ref={inputRef}
      value={rangeInput}
      label={label}
      hasError={showError}
      placeholder="e.g. 1-3, 6"
      onValueChange={handleRangeChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      message={showError ? 'Invalid page range' : undefined}
    />
  );
};

export default PageRangeInput;
